'use client';

/**
 * PresenceRibbon — v3 turn 5 warm persistent presence ribbon, replacing the
 * blue HandoffBanner treatment that used to sit above the input bar (see
 * handoff-banner.tsx, removed in the v3 cleanup).
 *
 * Pinned at the TOP of the conversation column (directly above
 * ConversationArea — see page.tsx) so the "we're finding you a guide" /
 * "you're talking to a human" status stays visible while the transcript
 * scrolls underneath it. This is ambient chrome for a LIVE handoff, distinct
 * from HandoffRecapCard (handoff-recap-card.tsx), which is a one-time
 * transcript entry shown once a handoff ends — never persistent.
 *
 * Copy comes from presence-ribbon-text.ts (pure, unit-tested); this
 * component only owns layout/color and the "End chat" action.
 */
import { HandoffPhase } from '@/types/chat';
import { useChatContext } from '@/contexts/ChatContext';
import { presenceRibbonText } from '@/lib/presence-ribbon-text';
import { cn } from '@/lib/utils';
import { IdentityAvatar } from './primitives/identity-avatar';

/**
 * Up-to-2-letter initials for the connected-state avatar. Falls back to 'A'
 * (Art of Living) — defensive only: HandoffPhase.CONNECTED always carries an
 * agentDisplayName in practice (useHandoff.ts's agentJoined and
 * agentMessageReceived both set CONNECTED and the name atomically), so this
 * fallback should be unreachable in normal operation.
 */
function getAgentInitials(name?: string): string {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  const initials = parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
  return initials || 'A';
}

interface PresenceRibbonProps {
  /** True once the TIMED_OUT "recovery window" has closed — see
   *  presence-ribbon-text.ts's hasResumedChatAfterTimeout and page.tsx's
   *  baseline-tracking effect. Ignored for every other phase. Defaults
   *  false so every existing call site (and this component's own tests)
   *  keeps showing the ribbon for the whole TIMED_OUT phase unless the
   *  caller opts in. */
  timeoutRecoveryWindowClosed?: boolean;
  /** Narrow-surface treatment (design 21h rule 6, "one truncation is a bug,
   *  not a style"). In a 400px panel the detail clause has nowhere to go and
   *  truncates mid-word — "· you can ke…" — so compact drops it entirely and
   *  tightens the row to ~34px, leaving the ribbon with the two jobs it can
   *  actually do at this width: state, and the exit.
   *
   *  Not merely cosmetic during COLLECTING in the embedded panel: the
   *  dropped clause reads "you can keep typing", which is FALSE there — the
   *  form step hides the input bar. The reassurance moves to the step's
   *  footer, reworded to match what's actually true. */
  compact?: boolean;
}

export function PresenceRibbon({
  timeoutRecoveryWindowClosed = false,
  compact = false,
}: PresenceRibbonProps = {}) {
  const { handoff, sendEndHandoff } = useChatContext();

  if (!handoff) return null;

  const copy = presenceRibbonText(
    handoff.phase,
    handoff.agentDisplayName,
    timeoutRecoveryWindowClosed
  );
  if (!copy) return null; // ENDED, or TIMED_OUT past its recovery window.

  // Compact keeps the same colours and semantics — only the row's height
  // budget and the detail clause change.
  const rowPad = compact ? 'px-[14px] py-[8px]' : 'px-6 py-[11px]';
  const titleSize = compact ? 'text-[12.5px]' : 'text-[13.5px]';
  const actionSize = compact ? 'text-[11.5px]' : 'text-[13px]';

  if (handoff.phase === HandoffPhase.TIMED_OUT) {
    // Static (non-pulsing) dot — distinct from the WAITING dot's animate-pulse,
    // since nothing is actively "in progress" anymore once the timeout has
    // fired. No action button: the handoff is already over server-side, so
    // there's nothing left to cancel (contrast with WAITING's "Cancel
    // request", which still has a live request to withdraw).
    return (
      <div
        role="status"
        className={cn(
          'flex w-full items-center gap-2.5 border-b border-[#F0DCC2] bg-[#FBEFE0]',
          rowPad
        )}
      >
        <span
          aria-hidden="true"
          className="h-[9px] w-[9px] shrink-0 rounded-full bg-[#D98A3D]"
        />
        <span
          className={cn(
            "font-['Work_Sans'] font-semibold text-[#9A7534]",
            titleSize
          )}
        >
          {copy.title}
        </span>
        {!compact && (
          <span className="min-w-0 flex-1 truncate font-['Work_Sans'] text-[13px] text-[#B08A4E]">
            {copy.detail}
          </span>
        )}
      </div>
    );
  }

  if (handoff.phase === HandoffPhase.CONNECTED) {
    return (
      <div
        role="status"
        className={cn(
          'flex w-full items-center justify-between gap-[12px] border-b border-[#D3E7D6] bg-[#EAF4EC]',
          rowPad
        )}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <IdentityAvatar
            initials={getAgentInitials(handoff.agentDisplayName)}
            size={compact ? 22 : 26}
            online
            dotRingColor="#EAF4EC"
          />
          <span
            className={cn(
              "min-w-0 truncate font-['Work_Sans'] font-semibold text-[#1F6B44]",
              compact ? 'text-[12.5px]' : 'text-[14px]'
            )}
          >
            {copy.title}
          </span>
          {!compact && (
            <span className="shrink-0 font-['Work_Sans'] text-[12.5px] text-[#4E8C68]">
              {copy.detail}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={sendEndHandoff}
          className={cn(
            "shrink-0 font-['Work_Sans'] font-semibold text-[#5b6072] underline-offset-2 hover:underline",
            actionSize
          )}
        >
          End chat
        </button>
      </div>
    );
  }

  // COLLECTING / WAITING — amber "looking for a guide" treatment. The drawn
  // design shows no exit control here, but the old HandoffBanner offered one
  // in every state — without it a user who changes their mind is stuck until
  // the no-agent timeout. "Cancel request" keeps that escape hatch (owner
  // decision, 2026-07-12); COLLECTING additionally has ContactInfoCard's own
  // Cancel, wired to the same sendEndHandoff.
  return (
    <div
      role="status"
      className={cn(
        'flex w-full items-center gap-2.5 border-b border-[#F0E0C7] bg-[#FBF1E2]',
        rowPad
      )}
    >
      <span
        aria-hidden="true"
        className="h-[9px] w-[9px] shrink-0 animate-pulse rounded-full bg-[#E8A94E]"
      />
      <span
        className={cn(
          "font-['Work_Sans'] font-semibold text-[#9A7534]",
          titleSize
        )}
      >
        {copy.title}
      </span>
      {!compact && (
        <span className="min-w-0 flex-1 truncate font-['Work_Sans'] text-[13px] text-[#B08A4E]">
          {copy.detail}
        </span>
      )}
      <button
        type="button"
        onClick={sendEndHandoff}
        className={cn(
          "ml-auto shrink-0 font-['Work_Sans'] font-semibold text-[#5b6072] underline-offset-2 hover:underline",
          actionSize
        )}
      >
        Cancel request
      </button>
    </div>
  );
}
